import React, { useMemo, useRef, useState } from 'react'
import ClearIcon from '@mui/icons-material/Clear'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { Box, Divider, IconButton, InputAdornment, Popover, TextField, Typography } from '@mui/material'

import { blue, grey } from '../../../colors'
import CustomTooltip from '../../../components/CustomTooltip'
import { type ISurveySection } from '../../../store/slices/SurveyTemplateSlice'
import { useScrollSpy } from './useScrollSpy'

const QUESTION_OUTLINE_LIMIT = 25
const SECTION_ONLY_OUTLINE_HELP = 'The survey template outline only shows section headers for templates with more than 25 questions.'

export function scrollToElement (id: string, offsetPx = 160): void {
  const el = document.getElementById(id)
  if (!el) return

  const rect = el.getBoundingClientRect()
  const y = rect.top + window.scrollY - offsetPx

  // clamp to document bounds
  const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
  const target = Math.min(Math.max(0, Math.round(y)), maxY)

  window.scrollTo({ top: target, behavior: 'smooth' })

  // optional quick highlight
  const prev = el.style.outline
  el.style.outline = `2px solid ${blue[500]}`
  el.style.transition = 'outline 0.3s'
  setTimeout(() => { el.style.outline = prev }, 800)
}

const RoadMapDivider = ({
  active, ariaLabel, onClick, sx
}: { active: boolean, ariaLabel: string, onClick: () => void, sx?: object }): React.ReactElement => (
  <Divider
    aria-label={ariaLabel}
    role="button"
    tabIndex={0}
    sx={{
      fontWeight: active ? 700 : 400,
      bgcolor: active ? blue[700] : undefined,
      color: active ? 'primary.contrastText' : undefined,
      height: active ? 4 : 2,
      transition: 'all 0.2s',
      cursor: 'pointer',
      ...sx
    }}
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onClick()
      }
    }}
  />
)

const RoadMapItem = ({
  label, active, onClick, sx
}: { label: string, active: boolean, onClick: () => void, sx?: object }): React.ReactElement => (
  <Typography
    component="button"
    type="button"
    sx={{
      background: 'transparent',
      border: 0,
      fontSize: '14px',
      cursor: 'pointer',
      color: active ? blue[700] : grey[50],
      display: 'block',
      fontWeight: active ? 700 : 400,
      lineHeight: 1.4,
      py: 0.5,
      px: 1,
      borderRadius: '4px',
      textAlign: 'left',
      width: '100%',
      '&:hover': { background: grey[100] },
      ...sx
    }}
    onClick={onClick}
  >
    {label}
  </Typography>
)

const getSectionLabel = (section: ISurveySection): string => (section.title ?? section.name) || ''

const normalizeSearchValue = (value: string): string => value.trim().toLowerCase()

interface OutlineRoadMapProps {
  surveySection: ISurveySection[]
}

const OutlineRoadMap: React.FC<OutlineRoadMapProps> = ({ surveySection }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const sidebarRef = useRef<HTMLDivElement>(null)

  const totalQuestionCount = useMemo(() => {
    return (surveySection ?? []).reduce((total, section) => total + (section.surveyItems?.length ?? 0), 0)
  }, [surveySection])

  const showQuestionEntries = totalQuestionCount <= QUESTION_OUTLINE_LIMIT

  const contentIds = useMemo(() => {
    const ids: string[] = []
    for (const section of surveySection ?? []) {
      if (section.surveySectionId) ids.push(section.surveySectionId)
      if (showQuestionEntries) {
        section.surveyItems?.forEach(item => { if (item.itemId) ids.push(item.itemId) })
      }
    }
    return ids
  }, [showQuestionEntries, surveySection])

  const filteredOutlineSections = useMemo(() => {
    const normalizedSearchTerm = normalizeSearchValue(searchTerm)

    return (surveySection ?? []).map(section => {
      const sectionLabel = getSectionLabel(section)
      const sectionMatchesSearch = normalizeSearchValue(sectionLabel).includes(normalizedSearchTerm)
      const surveyItems = showQuestionEntries
        ? section.surveyItems?.filter(item => {
          if (!item.itemId) return false
          if (!normalizedSearchTerm) return true
          return normalizeSearchValue(item.prompt).includes(normalizedSearchTerm)
        }) ?? []
        : []

      if (normalizedSearchTerm && !sectionMatchesSearch && surveyItems.length === 0) {
        return null
      }

      return {
        section,
        sectionLabel,
        surveyItems
      }
    }).filter((section): section is {
      section: ISurveySection
      sectionLabel: string
      surveyItems: NonNullable<ISurveySection['surveyItems']>
    } => section !== null)
  }, [searchTerm, showQuestionEntries, surveySection])

  const { activeId } = useScrollSpy(contentIds, 96)

  const scrollAndSetActive = (id: string): void => {
    scrollToElement(id)
  }

  const clearSearch = (): void => {
    setSearchTerm('')
  }

  const handleMouseEnter = (e?: React.MouseEvent<HTMLElement>): void => {
    if (e?.currentTarget) setAnchorEl(e.currentTarget)
  }
  const handleMouseLeave = (): void => { setAnchorEl(null) }
  const open = Boolean(anchorEl)

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 20,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 20
      }}
      ref={sidebarRef}
    >
      <Box
        aria-label="Survey outline navigation"
        role="navigation"
        sx={{ cursor: 'pointer' }}
        onMouseEnter={handleMouseEnter}
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
      >
        {surveySection?.map((section, sIdx) => {
          const sid = section.surveySectionId
          return (
            <React.Fragment key={sid ?? sIdx}>
              <RoadMapDivider
                ariaLabel={`Go to section ${getSectionLabel(section)}`}
                active={activeId === sid}
                onClick={() => { sid && scrollAndSetActive(sid) }}
                sx={{ mt: 2 }}
              />
              {showQuestionEntries && section.surveyItems?.map((item) => {
                const iid = item.itemId
                if (!iid) return null
                return (
                  <RoadMapDivider
                    key={iid}
                    ariaLabel={`Go to question ${item.prompt}`}
                    active={activeId === iid}
                    onClick={() => { iid && scrollAndSetActive(iid) }}
                    sx={{ mt: 2, width: 15, ml: 'auto' }}
                  />
                )
              })}
            </React.Fragment>
          )
        })}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleMouseLeave}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        PaperProps={{ sx: { p: 4, minWidth: 240, maxWidth: 340, borderRadius: 2, maxHeight: '70vh', overflowY: 'auto' } }}
        onMouseLeave={handleMouseLeave}
        elevation={2}
        disableRestoreFocus
        disableScrollLock
      >
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            placeholder="Search survey sections"
            value={searchTerm}
            onChange={(event) => { setSearchTerm(event.target.value) }}
            size="small"
            fullWidth
            inputProps={{ 'aria-label': 'Search survey sections' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm
                ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear survey section search"
                      edge="end"
                      onClick={clearSearch}
                      onMouseDown={(event) => { event.preventDefault() }}
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                  )
                : undefined
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                pl: 1.5
              },
              '& .MuiOutlinedInput-input': {
                pl: 0
              },
              '& .MuiInputAdornment-positionStart': {
                mr: 0
              }
            }}
          />
          {!showQuestionEntries && (
            <CustomTooltip title={SECTION_ONLY_OUTLINE_HELP} placement="top">
              <IconButton
                aria-label="Survey template outline help"
                size="small"
                sx={{ color: grey[600] }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </CustomTooltip>
          )}
        </Box>
        <Divider sx={{ borderColor: grey[200], my: 2 }} />
        {filteredOutlineSections.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No outline results found.
          </Typography>
        )}
        {filteredOutlineSections.map(({ section, sectionLabel, surveyItems }, sIdx) => {
          const sid = section.surveySectionId
          return (
            <React.Fragment key={sid ?? sIdx}>
              <RoadMapItem
                label={sectionLabel}
                active={activeId === sid}
                onClick={() => { sid && scrollAndSetActive(sid) }}
                sx={{ fontSize: '14px', fontWeight: 500 }}
              />
              {surveyItems.map((item) => {
                const iid = item.itemId
                if (!iid) return null
                return (
                  <RoadMapItem
                    key={iid}
                    label={item.prompt}
                    active={activeId === iid}
                    onClick={() => { iid && scrollAndSetActive(iid) }}
                    sx={{ fontSize: '12px', mb: 1, ml: 4 }}
                  />
                )
              })}
            </React.Fragment>
          )
        })}
      </Popover>
    </Box>
  )
}

export default OutlineRoadMap
